import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { LayoutDashboard, FlaskConical, FileText, Landmark, X } from 'lucide-react';
import { gql } from '@apollo/client';
import PeppermintLogo from './PeppermintLogo';

const GET_UNLINKED_DOCS_COUNT = gql`
  query GetUnlinkedDocsCount {
    documentOverview {
      unlinkedCount
    }
  }
`;

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const location = useLocation();

  const { data: docsData } = useQuery(GET_UNLINKED_DOCS_COUNT, {
    fetchPolicy: 'cache-and-network',
  });

  const unlinkedDocCount: number = docsData?.documentOverview?.unlinkedCount ?? 0;
  const unlinkedTxnCount = 0;

  const navigation = [
    {
      name: 'Summary',
      href: '/summary',
      icon: LayoutDashboard,
      current: location.pathname === '/summary',
      badge: 0,
    },
    {
      name: 'Studies',
      href: '/studies',
      icon: FlaskConical,
      current: location.pathname.startsWith('/studies'),
      badge: 0,
    },
    {
      name: 'Documents',
      href: '/documents',
      icon: FileText,
      current: location.pathname === '/documents',
      badge: unlinkedDocCount,
    },
    {
      name: 'Bank Accounts',
      href: '/bank-accounts',
      icon: Landmark,
      current: location.pathname.startsWith('/bank-accounts'),
      badge: unlinkedTxnCount,
    },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-600 bg-opacity-75 lg:hidden z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-6 bg-slate-900">
          <div className="flex items-center">
            <PeppermintLogo className="h-8 w-auto text-white" />
          </div>
          <button
            className="lg:hidden text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-8 px-4">
          <ul className="space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      item.current
                        ? 'bg-blue-50 text-blue-700 border-r-4 border-blue-700'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon
                      className={`mr-3 h-5 w-5 ${
                        item.current ? 'text-blue-700' : 'text-slate-400'
                      }`}
                    />
                    <span className="flex-1">{item.name}</span>
                    {item.badge > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
